import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { 
  CreditCard, Plus, Building2, Calendar, TrendingUp, AlertCircle,
  CheckCircle, XCircle, Clock, RefreshCw, Zap, Users, Database, Edit, Trash2, ArrowUpCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SubscriptionsPage = () => {
  const { getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  const [plans, setPlans] = useState([]);
  const [schoolSubscriptions, setSchoolSubscriptions] = useState([]);
  const [stats, setStats] = useState(null);
  const [schools, setSchools] = useState([]);
  const [upgradeRequests, setUpgradeRequests] = useState([]);
  
  const [planDialogOpen, setPlanDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [editingPlan, setEditingPlan] = useState(null);
  
  const [newPlan, setNewPlan] = useState({
    name: '',
    plan_type: 'basic',
    description: '',
    price_monthly: 0,
    price_yearly: 0,
    max_students: 100,
    max_teachers: 10,
    max_storage_gb: 5,
    features: '',
    modules_access: '',
    trial_days: 14
  });
  
  const [assignData, setAssignData] = useState({
    school_id: '',
    plan_id: '',
    billing_cycle: 'monthly',
    start_date: new Date().toISOString().split('T')[0],
    auto_renew: true
  });

  // Edit Subscription state
  const [editSubDialogOpen, setEditSubDialogOpen] = useState(false);
  const [editingSubscription, setEditingSubscription] = useState(null);
  const [editSubData, setEditSubData] = useState({
    plan_id: '',
    billing_cycle: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [plansRes, subsRes, statsRes, schoolsRes, upgradeRes] = await Promise.all([
        axios.get(`${API_URL}/api/subscriptions/plans?active_only=false`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/subscriptions/schools`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/subscriptions/stats`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/schools`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/subscriptions/upgrade-requests`, { headers: getAuthHeader() }).catch(() => ({ data: [] }))
      ]);
      
      // Sort plans by display_order (Basic=1, Standard=2, Premium=3)
      const sortedPlans = [...plansRes.data].sort((a, b) => {
        const orderA = a.display_order || (a.plan_type === 'basic' ? 1 : a.plan_type === 'standard' ? 2 : 3);
        const orderB = b.display_order || (b.plan_type === 'basic' ? 1 : b.plan_type === 'standard' ? 2 : 3);
        return orderA - orderB;
      });
      setPlans(sortedPlans);
      setSchoolSubscriptions(subsRes.data);
      setStats(statsRes.data);
      setSchools(schoolsRes.data);
      setUpgradeRequests(upgradeRes.data || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load subscription data');
    } finally {
      setLoading(false);
    }
  };

  const handleApproveUpgrade = async (requestId) => {
    try {
      await axios.post(`${API_URL}/api/subscriptions/upgrade-requests/${requestId}/approve`, {}, { headers: getAuthHeader() });
      toast.success('Upgrade request approved! Invoice has been generated.');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve upgrade request');
    }
  };

  const handleRejectUpgrade = async (requestId) => {
    const reason = window.prompt('Reason for rejection (optional):');
    try {
      await axios.post(`${API_URL}/api/subscriptions/upgrade-requests/${requestId}/reject?reason=${encodeURIComponent(reason || 'Rejected by admin')}`, {}, { headers: getAuthHeader() });
      toast.success('Upgrade request rejected');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject upgrade request');
    }
  };

  const handleCreatePlan = async () => {
    try {
      const planData = {
        ...newPlan,
        features: newPlan.features.split(',').map(f => f.trim()).filter(f => f),
        modules_access: newPlan.modules_access.split(',').map(m => m.trim()).filter(m => m)
      };
      
      await axios.post(`${API_URL}/api/subscriptions/plans`, planData, { headers: getAuthHeader() });
      toast.success('Subscription plan created');
      setPlanDialogOpen(false);
      setNewPlan({
        name: '', plan_type: 'basic', description: '', price_monthly: 0, price_yearly: 0,
        max_students: 100, max_teachers: 10, max_storage_gb: 5, features: '', modules_access: '', trial_days: 14
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to create plan');
    }
  };

  const handleAssignSubscription = async () => {
    try {
      await axios.post(`${API_URL}/api/subscriptions/assign`, assignData, { headers: getAuthHeader() });
      toast.success('Subscription assigned successfully');
      setAssignDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to assign subscription');
    }
  };

  const openEditPlanDialog = (plan) => {
    setEditingPlan(plan);
    setNewPlan({
      name: plan.name || '',
      plan_type: plan.plan_type || 'basic',
      description: plan.description || '',
      price_monthly: plan.price_monthly || 0,
      price_yearly: plan.price_yearly || 0,
      max_students: plan.max_students || 100,
      max_teachers: plan.max_teachers || 10,
      max_storage_gb: plan.max_storage_gb || 5,
      features: (plan.features || []).join(', '),
      modules_access: (plan.modules_access || []).join(', '),
      trial_days: plan.trial_days || 14
    });
    setPlanDialogOpen(true);
  };

  const handleUpdatePlan = async () => {
    try {
      const planData = {
        ...newPlan,
        features: newPlan.features.split(',').map(f => f.trim()).filter(f => f),
        modules_access: newPlan.modules_access.split(',').map(m => m.trim()).filter(m => m)
      };
      
      const response = await axios.put(`${API_URL}/api/subscriptions/plans/${editingPlan.id}`, planData, { headers: getAuthHeader() });
      
      // Show warning if subscriptions are affected
      if (response.data.affected_subscriptions > 0) {
        toast.success(`Plan updated! ${response.data.affected_subscriptions} existing subscription(s) will use new pricing on renewal.`);
      } else {
        toast.success('Subscription plan updated successfully');
      }
      
      setPlanDialogOpen(false);
      setEditingPlan(null);
      setNewPlan({
        name: '', plan_type: 'basic', description: '', price_monthly: 0, price_yearly: 0,
        max_students: 100, max_teachers: 10, max_storage_gb: 5, features: '', modules_access: '', trial_days: 14
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update plan');
    }
  };

  const handleDeletePlan = async (planId) => {
    if (!window.confirm('Are you sure you want to delete this plan?')) return;
    try {
      await axios.delete(`${API_URL}/api/subscriptions/plans/${planId}`, { headers: getAuthHeader() });
      toast.success('Plan deleted successfully');
      fetchData();
    } catch (error) {
      // Show specific error message for plans with active subscriptions
      const errorMsg = error.response?.data?.detail || 'Failed to delete plan';
      toast.error(errorMsg);
    }
  };

  const openEditSubscriptionDialog = (sub) => {
    setEditingSubscription(sub);
    setEditSubData({
      plan_id: sub.plan_id || '',
      billing_cycle: sub.billing_cycle || 'monthly'
    });
    setEditSubDialogOpen(true);
  };

  const handleUpdateSubscription = async () => {
    if (!editingSubscription) return;
    
    try {
      const response = await axios.patch(
        `${API_URL}/api/subscriptions/school/${editingSubscription.school_id}/subscription`,
        editSubData,
        { headers: getAuthHeader() }
      );
      
      if (response.data.status === 'updated') {
        const changes = response.data.changes.join(', ');
        toast.success(`Subscription updated: ${changes}`);
      } else {
        toast.info('No changes were made');
      }
      
      setEditSubDialogOpen(false);
      setEditingSubscription(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update subscription');
    }
  };

  const handleRenewSubscription = async (schoolId) => {
    try {
      await axios.post(`${API_URL}/api/subscriptions/renew/${schoolId}`, {}, { headers: getAuthHeader() });
      toast.success('Subscription renewed');
      fetchData();
    } catch (error) {
      toast.error('Failed to renew subscription');
    }
  };

  const handleCancelSubscription = async (schoolId) => {
    if (!window.confirm('Are you sure you want to cancel this subscription?')) return;
    try {
      await axios.patch(`${API_URL}/api/subscriptions/cancel/${schoolId}`, {}, { headers: getAuthHeader() });
      toast.success('Subscription cancelled');
      fetchData();
    } catch (error) {
      toast.error('Failed to cancel subscription');
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      active: { variant: 'default', icon: CheckCircle, color: 'text-green-600' },
      trial: { variant: 'secondary', icon: Clock, color: 'text-blue-600' },
      expired: { variant: 'destructive', icon: XCircle, color: 'text-red-600' },
      cancelled: { variant: 'outline', icon: XCircle, color: 'text-gray-600' },
      grace_period: { variant: 'warning', icon: AlertCircle, color: 'text-amber-600' }
    };
    const config = variants[status] || variants.expired;
    const Icon = config.icon;
    return (
      <Badge variant={config.variant} className="flex items-center gap-1">
        <Icon className={`h-3 w-3 ${config.color}`} />
        {status?.replace('_', ' ').toUpperCase()}
      </Badge>
    );
  };

  const getPlanTypeBadge = (type) => {
    const colors = {
      basic: 'bg-gray-100 text-gray-800',
      standard: 'bg-blue-100 text-blue-800',
      premium: 'bg-purple-100 text-purple-800',
      enterprise: 'bg-amber-100 text-amber-800'
    };
    return <Badge className={colors[type]}>{type?.toUpperCase()}</Badge>;
  };

  if (loading) {
    return (
      <DashboardLayout title="Subscriptions">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Subscription Management">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Subscription Management</h1>
            <p className="text-muted-foreground">Manage subscription plans and school subscriptions</p>
          </div>
          <div className="flex gap-2">
            <Dialog open={planDialogOpen} onOpenChange={(open) => {
              setPlanDialogOpen(open);
              if (!open) {
                setEditingPlan(null);
                setNewPlan({
                  name: '', plan_type: 'basic', description: '', price_monthly: 0, price_yearly: 0,
                  max_students: 100, max_teachers: 10, max_storage_gb: 5, features: '', modules_access: '', trial_days: 14
                });
              }
            }}>
              <DialogTrigger asChild>
                <Button data-testid="create-plan-btn">
                  <Plus className="h-4 w-4 mr-2" /> Create Plan
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>{editingPlan ? 'Edit Subscription Plan' : 'Create Subscription Plan'}</DialogTitle>
                </DialogHeader>
                <div className="grid grid-cols-2 gap-4 py-4">
                  <div className="space-y-2">
                    <Label>Plan Name</Label>
                    <Input value={newPlan.name} onChange={(e) => setNewPlan({...newPlan, name: e.target.value})} placeholder="e.g., Basic Plan" />
                  </div>
                  <div className="space-y-2">
                    <Label>Plan Type</Label>
                    <Select value={newPlan.plan_type} onValueChange={(v) => setNewPlan({...newPlan, plan_type: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="basic">Basic</SelectItem>
                        <SelectItem value="standard">Standard</SelectItem>
                        <SelectItem value="premium">Premium</SelectItem>
                        <SelectItem value="enterprise">Enterprise</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2 space-y-2">
                    <Label>Description</Label>
                    <Textarea value={newPlan.description} onChange={(e) => setNewPlan({...newPlan, description: e.target.value})} placeholder="Plan description..." />
                  </div>
                  <div className="space-y-2">
                    <Label>Monthly Price (₹)</Label>
                    <Input type="number" value={newPlan.price_monthly} onChange={(e) => setNewPlan({...newPlan, price_monthly: parseFloat(e.target.value)})} />
                  </div>
                  <div className="space-y-2">
                    <Label>Yearly Price (₹)</Label>
                    <Input type="number" value={newPlan.price_yearly} onChange={(e) => setNewPlan({...newPlan, price_yearly: parseFloat(e.target.value)})} />
                  </div>
                  <div className="space-y-2">
                    <Label>Max Students</Label>
                    <Input type="number" value={newPlan.max_students} onChange={(e) => setNewPlan({...newPlan, max_students: parseInt(e.target.value)})} />
                  </div>
                  <div className="space-y-2">
                    <Label>Max Teachers</Label>
                    <Input type="number" value={newPlan.max_teachers} onChange={(e) => setNewPlan({...newPlan, max_teachers: parseInt(e.target.value)})} />
                  </div>
                  <div className="space-y-2">
                    <Label>Storage (GB)</Label>
                    <Input type="number" value={newPlan.max_storage_gb} onChange={(e) => setNewPlan({...newPlan, max_storage_gb: parseFloat(e.target.value)})} />
                  </div>
                  <div className="space-y-2">
                    <Label>Trial Days</Label>
                    <Input type="number" value={newPlan.trial_days} onChange={(e) => setNewPlan({...newPlan, trial_days: parseInt(e.target.value)})} />
                  </div>
                  <div className="col-span-2 space-y-2">
                    <Label>Features (comma-separated)</Label>
                    <Input value={newPlan.features} onChange={(e) => setNewPlan({...newPlan, features: e.target.value})} placeholder="Feature 1, Feature 2, Feature 3" />
                  </div>
                  <div className="col-span-2 space-y-2">
                    <Label>Modules Access (comma-separated)</Label>
                    <Input value={newPlan.modules_access} onChange={(e) => setNewPlan({...newPlan, modules_access: e.target.value})} placeholder="students, teachers, fees, attendance" />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => {
                    setPlanDialogOpen(false);
                    setEditingPlan(null);
                  }}>Cancel</Button>
                  <Button onClick={editingPlan ? handleUpdatePlan : handleCreatePlan}>
                    {editingPlan ? 'Update Plan' : 'Create Plan'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            
            <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="assign-sub-btn">
                  <CreditCard className="h-4 w-4 mr-2" /> Assign Subscription
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Assign Subscription to School</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label>Select School</Label>
                    <Select value={assignData.school_id} onValueChange={(v) => setAssignData({...assignData, school_id: v})}>
                      <SelectTrigger><SelectValue placeholder="Choose a school" /></SelectTrigger>
                      <SelectContent>
                        {schools.map(school => (
                          <SelectItem key={school.id} value={school.id}>{school.name} ({school.school_code})</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Select Plan</Label>
                    <Select value={assignData.plan_id} onValueChange={(v) => setAssignData({...assignData, plan_id: v})}>
                      <SelectTrigger><SelectValue placeholder="Choose a plan" /></SelectTrigger>
                      <SelectContent>
                        {plans.filter(p => p.is_active).map(plan => (
                          <SelectItem key={plan.id} value={plan.id}>{plan.name} - ₹{plan.price_monthly}/mo</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Billing Cycle</Label>
                    <Select value={assignData.billing_cycle} onValueChange={(v) => setAssignData({...assignData, billing_cycle: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="monthly">Monthly</SelectItem>
                        <SelectItem value="quarterly">Quarterly (10% off)</SelectItem>
                        <SelectItem value="yearly">Yearly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Start Date</Label>
                    <Input type="date" value={assignData.start_date} onChange={(e) => setAssignData({...assignData, start_date: e.target.value})} />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setAssignDialogOpen(false)}>Cancel</Button>
                  <Button onClick={handleAssignSubscription}>Assign</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-blue-600" /> Total Schools
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{stats.total_schools}</div>
                </CardContent>
              </Card>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" /> Active
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-green-600">{stats.active_subscriptions}</div>
                </CardContent>
              </Card>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <Clock className="h-4 w-4 text-blue-600" /> Trial
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-blue-600">{stats.trial_subscriptions}</div>
                </CardContent>
              </Card>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 text-amber-600" /> Expiring Soon
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-amber-600">{stats.expiring_soon}</div>
                </CardContent>
              </Card>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
              <Card className="bg-gradient-to-br from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 border-purple-200 dark:border-purple-800">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium flex items-center gap-2 text-purple-700 dark:text-purple-300">
                    <TrendingUp className="h-4 w-4" /> Est. Revenue
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="text-xl font-bold text-purple-600">
                    ₹{Math.round(stats.estimated_monthly_revenue || 0).toLocaleString('en-IN')}<span className="text-sm font-normal text-purple-500">/mo</span>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="overview">School Subscriptions</TabsTrigger>
            <TabsTrigger value="plans">Subscription Plans</TabsTrigger>
            <TabsTrigger value="upgrades" className="relative">
              Upgrade Requests
              {upgradeRequests.filter(r => r.status === 'pending').length > 0 && (
                <span className="ml-2 bg-red-500 text-white text-xs rounded-full px-1.5 py-0.5">
                  {upgradeRequests.filter(r => r.status === 'pending').length}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          {/* School Subscriptions Tab */}
          <TabsContent value="overview">
            <Card>
              <CardHeader>
                <CardTitle>School Subscriptions</CardTitle>
                <CardDescription>All schools and their subscription status</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>School</TableHead>
                      <TableHead>Plan</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Billing</TableHead>
                      <TableHead>End Date</TableHead>
                      <TableHead>Days Left</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {schoolSubscriptions.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                          No subscriptions found
                        </TableCell>
                      </TableRow>
                    ) : (
                      schoolSubscriptions.map(sub => (
                        <TableRow key={sub.id}>
                          <TableCell>
                            <div>
                              <p className="font-medium">{sub.school?.name}</p>
                              <p className="text-xs text-muted-foreground">{sub.school?.school_code}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            {sub.plan && getPlanTypeBadge(sub.plan.plan_type)}
                            <p className="text-xs mt-1">{sub.plan?.name}</p>
                          </TableCell>
                          <TableCell>{getStatusBadge(sub.status)}</TableCell>
                          <TableCell className="capitalize">{sub.billing_cycle}</TableCell>
                          <TableCell>{sub.end_date}</TableCell>
                          <TableCell>
                            <span className={sub.days_left <= 7 ? 'text-red-600 font-medium' : ''}>
                              {sub.days_left} days
                            </span>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <Button size="sm" variant="outline" onClick={() => openEditSubscriptionDialog(sub)} title="Edit Subscription">
                                <Edit className="h-3 w-3" />
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => handleRenewSubscription(sub.school_id)} title="Renew">
                                <RefreshCw className="h-3 w-3" />
                              </Button>
                              <Button size="sm" variant="destructive" onClick={() => handleCancelSubscription(sub.school_id)} title="Cancel">
                                <XCircle className="h-3 w-3" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Plans Tab */}
          <TabsContent value="plans">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {plans.map(plan => (
                <motion.div key={plan.id} initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}>
                  <Card className={!plan.is_active ? 'opacity-50' : ''}>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-lg">{plan.name}</CardTitle>
                        {getPlanTypeBadge(plan.plan_type)}
                      </div>
                      <CardDescription>{plan.description}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <div>
                          <p className="text-3xl font-bold">₹{plan.price_monthly}<span className="text-sm font-normal text-muted-foreground">/mo</span></p>
                          <p className="text-sm text-muted-foreground">₹{plan.price_yearly}/year</p>
                        </div>
                        <div className="space-y-2 text-sm">
                          <div className="flex items-center gap-2">
                            <Users className="h-4 w-4 text-muted-foreground" />
                            <span>Up to {plan.max_students} students</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Users className="h-4 w-4 text-muted-foreground" />
                            <span>Up to {plan.max_teachers} teachers</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Database className="h-4 w-4 text-muted-foreground" />
                            <span>{plan.max_storage_gb} GB storage</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Zap className="h-4 w-4 text-muted-foreground" />
                            <span>{plan.trial_days} days trial</span>
                          </div>
                        </div>
                        {plan.features?.length > 0 && (
                          <div className="pt-2 border-t">
                            <p className="text-xs font-medium mb-2">Features:</p>
                            <div className="flex flex-wrap gap-1">
                              {plan.features.slice(0, 3).map((f, i) => (
                                <Badge key={i} variant="outline" className="text-xs">{f}</Badge>
                              ))}
                              {plan.features.length > 3 && (
                                <Badge variant="outline" className="text-xs">+{plan.features.length - 3}</Badge>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </CardContent>
                    <div className="px-6 pb-4 flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="flex-1"
                        onClick={() => openEditPlanDialog(plan)}
                      >
                        <Edit className="h-4 w-4 mr-1" /> Edit
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => handleDeletePlan(plan.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </Card>
                </motion.div>
              ))}
              
              {plans.length === 0 && (
                <Card className="col-span-full">
                  <CardContent className="flex flex-col items-center justify-center py-12">
                    <CreditCard className="h-12 w-12 text-muted-foreground/30 mb-4" />
                    <p className="text-muted-foreground">No subscription plans created yet</p>
                    <Button className="mt-4" onClick={() => setPlanDialogOpen(true)}>
                      <Plus className="h-4 w-4 mr-2" /> Create First Plan
                    </Button>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Upgrade Requests Tab */}
          <TabsContent value="upgrades">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ArrowUpCircle className="h-5 w-5" />
                  Upgrade Requests
                </CardTitle>
                <CardDescription>Manage upgrade requests from schools</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>School</TableHead>
                      <TableHead>Current Plan</TableHead>
                      <TableHead>Requested Plan</TableHead>
                      <TableHead>Billing Change</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {upgradeRequests.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                          No upgrade requests found
                        </TableCell>
                      </TableRow>
                    ) : (
                      upgradeRequests.map(req => (
                        <TableRow key={req.id}>
                          <TableCell>
                            <p className="font-medium">{req.school_name}</p>
                          </TableCell>
                          <TableCell>
                            <p className="text-sm">{req.current_plan_name}</p>
                          </TableCell>
                          <TableCell>
                            <p className="text-sm font-medium text-green-600">{req.new_plan_name}</p>
                          </TableCell>
                          <TableCell>
                            <p className="text-sm">
                              {req.current_billing_cycle} → {req.new_billing_cycle}
                            </p>
                          </TableCell>
                          <TableCell>
                            <p className="font-semibold">₹{req.amount?.toLocaleString()}</p>
                          </TableCell>
                          <TableCell>
                            {req.status === 'pending' && (
                              <Badge variant="outline" className="bg-yellow-100 text-yellow-800">Pending</Badge>
                            )}
                            {req.status === 'approved' && (
                              <Badge className="bg-blue-500">Approved</Badge>
                            )}
                            {req.status === 'completed' && (
                              <Badge className="bg-green-500">Completed</Badge>
                            )}
                            {req.status === 'rejected' && (
                              <Badge variant="destructive">Rejected</Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            <p className="text-xs text-muted-foreground">
                              {new Date(req.created_at).toLocaleDateString()}
                            </p>
                          </TableCell>
                          <TableCell>
                            {req.status === 'pending' && (
                              <div className="flex gap-2">
                                <Button 
                                  size="sm" 
                                  className="bg-green-600 hover:bg-green-700"
                                  onClick={() => handleApproveUpgrade(req.id)}
                                  data-testid="approve-upgrade-btn"
                                >
                                  <CheckCircle className="h-3 w-3 mr-1" /> Approve
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="destructive"
                                  onClick={() => handleRejectUpgrade(req.id)}
                                  data-testid="reject-upgrade-btn"
                                >
                                  <XCircle className="h-3 w-3 mr-1" /> Reject
                                </Button>
                              </div>
                            )}
                            {req.status === 'approved' && req.invoice_id && (
                              <Badge variant="outline">Invoice Sent</Badge>
                            )}
                            {req.status === 'completed' && (
                              <Badge variant="outline" className="bg-green-50">Done</Badge>
                            )}
                            {req.status === 'rejected' && (
                              <p className="text-xs text-muted-foreground">{req.rejected_reason}</p>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Edit Subscription Dialog */}
        <Dialog open={editSubDialogOpen} onOpenChange={setEditSubDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Subscription</DialogTitle>
            </DialogHeader>
            {editingSubscription && (
              <div className="space-y-4 py-4">
                <div className="p-4 bg-muted rounded-lg">
                  <p className="font-semibold">{editingSubscription.school?.name}</p>
                  <p className="text-sm text-muted-foreground">
                    Current: {editingSubscription.plan?.name} • {editingSubscription.billing_cycle?.charAt(0).toUpperCase() + editingSubscription.billing_cycle?.slice(1)}
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Change Plan</Label>
                  <Select 
                    value={editSubData.plan_id} 
                    onValueChange={(v) => setEditSubData({...editSubData, plan_id: v})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select new plan" />
                    </SelectTrigger>
                    <SelectContent>
                      {plans.map(plan => (
                        <SelectItem key={plan.id} value={plan.id}>
                          {plan.name} - ₹{plan.price_monthly}/mo
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Billing Cycle</Label>
                  <Select 
                    value={editSubData.billing_cycle} 
                    onValueChange={(v) => setEditSubData({...editSubData, billing_cycle: v})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="quarterly">Quarterly (10% off)</SelectItem>
                      <SelectItem value="yearly">Yearly (Best Value)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Changing billing cycle will update the subscription end date
                  </p>
                </div>

                <DialogFooter>
                  <Button variant="outline" onClick={() => setEditSubDialogOpen(false)}>Cancel</Button>
                  <Button onClick={handleUpdateSubscription}>Update Subscription</Button>
                </DialogFooter>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default SubscriptionsPage;
